<script lang="ts">
  import { onDestroy } from 'svelte';
  import { encryptToTlck, parseTlck, type Policy } from '../lib/tlck.js';
  import { generateOwnerKeypair, encodeKeyFile } from '../lib/owner.js';
  import { createLock } from '../lib/api.js';
  import { queue, activeTab, currentPage } from '../lib/store.js';
  import { hexdec, utf8 } from '../lib/bytes.js';
  import { CAN_SHARE_FILES, shareFile, setDragDownloadUrl } from '../lib/platform.js';
  import { t } from '../lib/i18n.js';

  type Step = 'idle' | 'encrypting' | 'done' | 'error';

  let file: File | null = $state(null);
  let policy: Policy = $state('time');
  let unlockLocal: string = $state(defaultUnlockLocal());
  let step: Step = $state('idle');
  let errorMsg = $state('');
  let tlckBytes: Uint8Array | null = $state(null);
  let tlckName = $state('');
  let keyFileText: string | null = $state(null);
  let keyFileName = $state('');
  let tlckBlobUrl: string | null = $state(null);
  let keyBlobUrl: string | null = $state(null);
  let dragging = $state(false);

  const LARGE_BYTES = 50 * 1024 * 1024;
  const TLCK_MIME = 'application/octet-stream';
  const KEY_MIME = 'application/x-pem-file';

  function revokeUrls() {
    if (tlckBlobUrl) { URL.revokeObjectURL(tlckBlobUrl); tlckBlobUrl = null; }
    if (keyBlobUrl)  { URL.revokeObjectURL(keyBlobUrl);  keyBlobUrl = null; }
  }
  onDestroy(revokeUrls);

  function defaultUnlockLocal(): string {
    const d = new Date(Date.now() + 10 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function pickFile(ev: Event) {
    const input = ev.currentTarget as HTMLInputElement;
    file = input.files?.[0] ?? null;
  }

  function onDrop(ev: DragEvent) {
    ev.preventDefault();
    dragging = false;
    const f = ev.dataTransfer?.files?.[0];
    if (f) file = f;
  }

  function policyDescription(p: Policy): string {
    if (p === 'time') return $t('encrypt.desc_time');
    if (p === 'manual') return $t('encrypt.desc_manual');
    return $t('encrypt.desc_time_manual');
  }

  function needsTime(p: Policy): boolean { return p !== 'manual'; }
  function needsManual(p: Policy): boolean { return p !== 'time'; }

  function translateError(msg: string): string {
    const key = `errors.${msg}`;
    const translated = $t(key);
    return translated !== key ? translated : msg;
  }

  async function encrypt() {
    errorMsg = '';
    if (!file) { errorMsg = $t('encrypt.err_no_file'); return; }

    let unlockUnix: number | undefined;
    if (needsTime(policy)) {
      const ts = new Date(unlockLocal).getTime();
      if (!Number.isFinite(ts)) { errorMsg = $t('encrypt.err_invalid_time'); return; }
      if (ts <= Date.now() + 60_000 - 1000) { errorMsg = $t('encrypt.err_future_time'); return; }
      unlockUnix = Math.floor(ts / 1000);
    }

    step = 'encrypting';
    try {
      const fileBytes = new Uint8Array(await file.arrayBuffer());

      let lockId: Uint8Array | undefined;
      let releasePub: Uint8Array | undefined;

      if (needsManual(policy)) {
        const owner = await generateOwnerKeypair();

        const { lockIdHex, releasePubkey } = await createLock({
          policy: policy === 'manual' ? 'manual' : 'time_manual',
          ownerPubkey: owner.spkiPub,
          unlockUnix,
        });
        lockId = hexdec(lockIdHex);
        releasePub = releasePubkey;

        keyFileText = encodeKeyFile(lockIdHex, owner.pkcs8Priv);
        keyFileName = `${file.name}.tlck-key`;
      }

      const { tlckBytes: tb } = await encryptToTlck({
        fileBytes,
        fileName: file.name,
        fileMime: file.type || 'application/octet-stream',
        policy,
        unlockUnix,
        lockId,
        releasePubkey: releasePub,
      });

      tlckBytes = tb;
      tlckName = `${file.name}.tlck`;

      revokeUrls();
      tlckBlobUrl = URL.createObjectURL(new Blob([tb as BlobPart], { type: TLCK_MIME }));
      if (keyFileText) {
        keyBlobUrl = URL.createObjectURL(
          new Blob([utf8(keyFileText) as BlobPart], { type: KEY_MIME }),
        );
      }

      const parsed = await parseTlck(tb);
      queue.add({
        id: crypto.randomUUID(),
        file: null,
        tlckBytes: tb,
        parsed,
        state: policy === 'manual' ? 'waiting-manual' : (unlockUnix! * 1000 <= Date.now() ? 'ready' : 'waiting'),
        addedAt: Date.now(),
      });

      step = 'done';
    } catch (e) {
      console.error(e);
      errorMsg = e instanceof Error ? translateError(e.message) : $t('encrypt.err_unknown');
      step = 'error';
    }
  }

  function downloadFromUrl(url: string, name: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  function reset() {
    revokeUrls();
    file = null;
    policy = 'time';
    unlockLocal = defaultUnlockLocal();
    step = 'idle';
    errorMsg = '';
    tlckBytes = null;
    keyFileText = null;
    tlckName = '';
    keyFileName = '';
  }

  function policyConfirmMessage(): string {
    if (policy === 'time') return $t('encrypt.confirm_time');
    if (policy === 'manual') return $t('encrypt.confirm_manual');
    return $t('encrypt.confirm_time_manual');
  }

  function gotoDecrypt() {
    currentPage.set('app');
    activeTab.set('decrypt');
  }
</script>

<div class="col">
  {#if step === 'encrypting'}
    <div class="encrypt-anim">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.7">
        <rect x="4" y="11" width="16" height="10" rx="2"/>
        <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
      </svg>
      <div class="encrypt-anim-dots">
        <span></span><span></span><span></span>
      </div>
      <div class="small dim">{$t('encrypt.encrypting')}</div>
    </div>

  {:else if step === 'done' && tlckBytes}
    <div class="encrypt-result">
      <div class="file-cards" class:single={!keyFileText}>
        <div
          class="file-card tlck"
          role="group"
          aria-label={$t('encrypt.aria_encrypted_file', { name: tlckName })}
          draggable="true"
          ondragstart={(e) => setDragDownloadUrl(e, tlckBlobUrl, tlckName, TLCK_MIME)}
          title={$t('encrypt.title_drag')}
        >
          <div class="file-card-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="4" y="11" width="16" height="10" rx="2"/>
              <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
            </svg>
          </div>
          <div class="file-card-name">{tlckName}</div>
          <div class="file-card-meta">{formatBytes(tlckBytes.length)}</div>
          <div class="file-card-actions">
            {#if CAN_SHARE_FILES}
              <button
                class="icon-btn"
                draggable="false"
                title={$t('encrypt.title_share')}
                aria-label={$t('encrypt.title_share')}
                onclick={() => shareFile(tlckBytes!, tlckName, TLCK_MIME)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="18" cy="5" r="3"/>
                  <circle cx="6" cy="12" r="3"/>
                  <circle cx="18" cy="19" r="3"/>
                  <line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/>
                  <line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/>
                </svg>
              </button>
            {/if}
            <button
              class="icon-btn"
              draggable="false"
              title={$t('encrypt.title_save')}
              aria-label={$t('encrypt.title_save')}
              onclick={() => tlckBlobUrl && downloadFromUrl(tlckBlobUrl, tlckName)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 4v12"/>
                <path d="M8 12l4 4 4-4"/>
                <path d="M5 20h14"/>
              </svg>
            </button>
          </div>
        </div>

        {#if keyFileText}
          <div
            class="file-card key"
            role="group"
            aria-label={$t('encrypt.aria_key_file', { name: keyFileName })}
            draggable="true"
            ondragstart={(e) => setDragDownloadUrl(e, keyBlobUrl, keyFileName, KEY_MIME)}
            title={$t('encrypt.title_drag')}
          >
            <div class="file-card-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="15.5" cy="8.5" r="4"/>
                <path d="M12.5 11.5L3.5 20.5"/>
                <path d="M7 17l2 2"/>
                <path d="M10 14l2 2"/>
              </svg>
            </div>
            <div class="file-card-name">{keyFileName}</div>
            <div class="file-card-meta">
              {#if policy === 'manual'}
                {$t('encrypt.key_meta_manual')}
              {:else}
                {$t('encrypt.key_meta_time_manual')}
              {/if}
            </div>
            <div class="file-card-actions">
              <button
                class="icon-btn"
                draggable="false"
                title={$t('encrypt.title_save')}
                aria-label={$t('encrypt.title_save')}
                onclick={() => keyBlobUrl && downloadFromUrl(keyBlobUrl, keyFileName)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 4v12"/>
                  <path d="M8 12l4 4 4-4"/>
                  <path d="M5 20h14"/>
                </svg>
              </button>
            </div>
          </div>
        {/if}
      </div>

      <div class="file-cards-caption">{policyConfirmMessage()}</div>

      <div class="row">
        <button onclick={reset}>{$t('encrypt.btn_reset')}</button>
        <button class="primary" onclick={gotoDecrypt}>{$t('encrypt.btn_goto_decrypt')}</button>
      </div>
    </div>

  {:else}
    <div class="step">
      <h3><span class="step-pill">1</span> {$t('encrypt.step1')}</h3>
      <label
        class="dropzone"
        class:drag={dragging}
        ondragover={(e) => { e.preventDefault(); dragging = true; }}
        ondragleave={() => { dragging = false; }}
        ondrop={onDrop}
      >
        <input type="file" style="display:none" onchange={pickFile} />
        {#if file}
          <strong>{file.name}</strong>
          <div class="small dim">{(file.size / 1024).toFixed(1)} KB</div>
          {#if file.size > LARGE_BYTES}
            <div class="alert warn" style="margin-top:10px">{$t('encrypt.large_file')}</div>
          {/if}
        {:else}
          {$t('encrypt.dropzone')}
        {/if}
      </label>
    </div>

    <div class="step">
      <h3><span class="step-pill">2</span> {$t('encrypt.step2')}</h3>
      <div class="policy">
        <button class:active={policy === 'time'} onclick={() => (policy = 'time')}>{$t('encrypt.policy_time')}</button>
        <button class:active={policy === 'time_manual'} onclick={() => (policy = 'time_manual')}>{$t('encrypt.policy_time_manual')}</button>
        <button class:active={policy === 'manual'} onclick={() => (policy = 'manual')}>{$t('encrypt.policy_manual')}</button>
      </div>
      <div class="small dim">{policyDescription(policy)}</div>
    </div>

    {#if needsTime(policy)}
      <div class="step">
        <h3><span class="step-pill">3</span> {$t('encrypt.step3')}</h3>
        <input type="datetime-local" bind:value={unlockLocal} />
      </div>
    {/if}

    <div class="step">
      <h3><span class="step-pill">{needsTime(policy) ? '4' : '3'}</span> {$t('encrypt.step4')}</h3>
      <div class="row">
        <button class="primary" disabled={!file} onclick={encrypt}>
          {$t('encrypt.btn_encrypt')}
        </button>
        {#if errorMsg}<div class="alert error">{errorMsg}</div>{/if}
      </div>
    </div>
  {/if}
</div>
